import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { assocPath, path } from 'ramda';
import { atom, useAtom } from 'jotai';
import JSURL from 'jsurl';
import equal from 'fast-deep-equal';
import type { LocationDescriptor } from 'history';

function getUrlParamValues(searchString: string) {
    return Object.fromEntries(new URLSearchParams(searchString).entries());
}

const urlData = () =>
    JSURL.tryParse(getUrlParamValues(window.location.search)?.[rootDataKey] ?? {}, {});

const rootDataKey = 'state';

const newLocation = (pathname: string, newVal): LocationDescriptor => ({
    pathname,
    search: `?${rootDataKey}=${JSURL.stringify(newVal)}`,
});

const getValue = (data: Record<string, any>, keyPath: string, defaultValue: any = null): any =>
    path(keyPath.split('.'), data) ?? defaultValue;

// its impossible to listen to browser location 'search' changes so just putting this little hack in
// incrementing the count every time we push new state in and resetting when the path changes
const counter = atom(0);

function useUrlState() {
    const $history = useHistory();
    const { pathname, search } = useLocation();
    const [count, setCount] = useAtom(counter);

    useEffect(() => {
        setCount(0);
    }, [pathname, setCount]);

    const get = useCallback(
        (keyPath: string, defaultValue: any = null) => getValue(urlData(), keyPath, defaultValue),
        // want to invalidate the callback when the count changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [count, search],
    );

    const replace = useCallback(
        (keyPath: string, value) => {
            const pathParts = keyPath.split('.');
            const newVal = assocPath(pathParts, value, urlData());

            $history.replace(newLocation(pathname, newVal));
        },
        // want to invalidate the callback when the count changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [$history, pathname, count, search],
    );

    /**
     * Write several keys at once. `mode: 'push'` adds a history entry (a user navigating);
     * `mode: 'replace'` rewrites the current one. Effects that fill in defaults or correct invalid
     * state must replace: a push there means Back returns to the URL that immediately pushes forward
     * again, and the user is stuck.
     */
    const writeMany = useCallback(
        (data: Record<string, any>, mode: 'push' | 'replace') => {
            const newLocationData = Object.entries(data).reduce((acc, [keyPath, value]) => {
                if (!equal(getValue(acc, keyPath), value)) {
                    const pathParts = keyPath.split('.');
                    return assocPath(pathParts, value, acc);
                }

                return acc;
            }, urlData());

            if (!equal(urlData(), newLocationData)) {
                $history[mode](newLocation(pathname, newLocationData));
            }
            setCount(count + 1);
        },
        // `search` isn't read (urlData() reads window.location), but like get/replace the callback
        // is meant to be a new function whenever the URL changes, so callers' memos see the change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [$history, pathname, setCount, count, search],
    );

    const pushMany = useCallback(
        (data: Record<string, any>) => writeMany(data, 'push'),
        [writeMany],
    );
    const replaceMany = useCallback(
        (data: Record<string, any>) => writeMany(data, 'replace'),
        [writeMany],
    );

    const push = useCallback(
        (keyPath: string, value) => {
            pushMany({ [keyPath]: value });
        },
        [pushMany],
    );

    return useMemo(
        () => ({ push, pushMany, replace, replaceMany, get }),
        [get, push, pushMany, replace, replaceMany],
    );
}

export default useUrlState;
